import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { berlinDate, TASK_GROUPS, TASK_GROUP_LABELS, type Paginated, type Task, type TaskGroup, type TaskSummary } from '@kontor/shared';
import { api, ApiError, queryString } from '../../api';
import { FilterBar, type FilterField } from '../../components/filter/FilterBar';
import { useSession } from '../../session';
import { formatDate, useAssignees } from './assignees';
import { CalendarTabs } from './CalendarTabs';
import './calendar.css';

/** Stand einer Aufgabe nach ihrer Fälligkeit: überfällig (rot), heute (gelb), in den nächsten 7 Tagen (blau), später oder ohne Datum (neutral). Erledigte Aufgaben haben keinen Stand. */
type Urgency = 'overdue' | 'today' | 'soon' | 'later' | 'none';
function urgencyOf(dueDate: string | null, today: string): { state: Urgency; hint: string | null } {
  if (!dueDate) return { state: 'none', hint: null };
  const days = Math.round((Date.parse(`${dueDate}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000);
  if (days < 0) return { state: 'overdue', hint: days === -1 ? 'seit gestern' : `seit ${-days} Tagen` };
  if (days === 0) return { state: 'today', hint: 'heute' };
  if (days === 1) return { state: 'soon', hint: 'morgen' };
  if (days <= 7) return { state: 'soon', hint: `in ${days} Tagen` };
  return { state: 'later', hint: `in ${days} Tagen` };
}
const URGENCY_LABEL: Record<Urgency, string> = { overdue: 'Überfällig', today: 'Heute fällig', soon: 'Offen', later: 'Offen', none: 'Offen' };

const completedFormat = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short' });

/** Unter dieser Breite (siehe styles.css) erscheint die Liste als Karten, und die Aktionen stecken hinter Wischen oder langem Drücken */
const isCardLayout = () => window.matchMedia('(max-width: 719px)').matches;

const icon = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', width: 20, height: 20, viewBox: '0 0 24 24', 'aria-hidden': true } as const;
const CheckIcon = () => <svg {...icon}><path d="M20 6 9 17l-5-5" /></svg>;
const UndoIcon = () => <svg {...icon}><path d="M3 7v6h6" /><path d="M21 17a9 9 0 0 0-15-6.7L3 13" /></svg>;
const EditIcon = () => <svg {...icon}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>;
const TrashIcon = () => <svg {...icon}><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /></svg>;

/**
 * Liste der Aufgaben, gegliedert nach Fälligkeit. Gruppe, „Meine Aufgaben“, Suche und Seite stehen in der Adresszeile.
 * Was „heute“ ist, bestimmt der Server in der Zeitzone des Betriebs, deshalb schickt diese Seite nur die Gruppe.
 */
export function TasksPage() {
  const [params, setParams] = useSearchParams();
  const { can, session } = useSession();
  const client = useQueryClient();
  const assignees = useAssignees();
  const group = (params.get('group') ?? 'open') as TaskGroup;
  const today = berlinDate();
  const mine = params.get('mine') === '1';
  const page = Number(params.get('page') ?? 1);
  const q = params.get('q') ?? '';
  const assignedUserId = params.get('assigned') || undefined;
  const participantUserId = params.get('participant') || undefined;
  // „Meine Aufgaben“ sind die, bei denen man zuständig oder beteiligt ist
  const involvedUserId = mine ? session?.user.id : undefined;

  const summary = useQuery({
    queryKey: ['tasks', 'summary', { involvedUserId, assignedUserId, participantUserId }],
    queryFn: async () => (await api<{ data: TaskSummary }>('GET', `/tasks/summary${queryString({ involvedUserId, assignedUserId, participantUserId })}`)).data,
  });
  const list = useQuery({
    queryKey: ['tasks', { group, involvedUserId, assignedUserId, participantUserId, q, page }],
    queryFn: () => api<Paginated<Task>>('GET', `/tasks${queryString({ group, involvedUserId, assignedUserId, participantUserId, q, page, perPage: 25 })}`),
    placeholderData: keepPreviousData,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api('DELETE', `/tasks/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['tasks'] }),
  });

  // Auf dem Handy zeigt eine Karte ihre Aktionen nach langem Drücken oder Wischen nach links. Es ist immer höchstens eine Karte offen.
  const [revealed, setRevealed] = useState<string | null>(null);
  const gesture = useRef<{ x: number; y: number; id: string; timer: number | null } | null>(null);
  const suppressClick = useRef(false);

  const reveal = (id: string) => {
    suppressClick.current = true;
    window.setTimeout(() => (suppressClick.current = false), 400); // falls nach der Geste kein Klick mehr kommt
    setRevealed(id);
  };
  const cancelTimer = () => {
    if (gesture.current?.timer) window.clearTimeout(gesture.current.timer);
    gesture.current = null;
  };
  const onPointerDown = (e: ReactPointerEvent, id: string) => {
    if (!isCardLayout() || (e.target as HTMLElement).closest('.task-actions')) return;
    cancelTimer();
    gesture.current = { x: e.clientX, y: e.clientY, id, timer: window.setTimeout(() => reveal(id), 500) };
  };
  const onPointerMove = (e: ReactPointerEvent) => {
    const g = gesture.current;
    if (!g) return;
    const dx = e.clientX - g.x;
    const dy = e.clientY - g.y;
    // Wer sich bewegt, drückt nicht lange. Ein deutlicher Zug nach links zeigt die Aktionen, einer nach rechts blendet sie aus.
    if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
      if (g.timer) window.clearTimeout(g.timer);
      g.timer = null;
    }
    if (dx < -50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      reveal(g.id);
      gesture.current = null;
    } else if (dx > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      setRevealed(null);
      gesture.current = null;
    }
  };
  // Ein Tipp neben die geöffnete Karte oder Scrollen schließt sie
  useEffect(() => {
    if (!revealed) return;
    const close = (e: PointerEvent) => {
      if (!(e.target as Element).closest('.task-row.is-revealed')) setRevealed(null);
    };
    const hide = () => setRevealed(null);
    document.addEventListener('pointerdown', close);
    window.addEventListener('scroll', hide, { passive: true });
    return () => {
      document.removeEventListener('pointerdown', close);
      window.removeEventListener('scroll', hide);
    };
  }, [revealed]);

  const toggle = useMutation({
    // Der Weg „progress“ steht auch der zuständigen Person offen, die Aufgaben sonst nur lesen darf
    mutationFn: (task: Task) => api<{ data: Task }>('PATCH', `/tasks/${task.id}/progress`, { status: task.status === 'done' ? 'open' : 'done' }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['tasks'] }),
  });

  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v);
      else merged.delete(k);
    }
    if (!('page' in next)) merged.delete('page');
    setParams(merged);
  };

  const canWrite = can('calendar.write');
  // Die Zahlen stehen in der Auswahl, damit man sieht, wo etwas wartet, ohne den Bereich durchzuklicken
  const count = (g: TaskGroup) => (summary.data ? ` (${summary.data[g]})` : '');
  const fields: FilterField[] = [
    {
      key: 'group',
      label: 'Fälligkeit',
      type: 'select',
      options: TASK_GROUPS.map((g) => ({ value: g === 'open' ? '' : g, label: `${TASK_GROUP_LABELS[g]}${count(g)}` })),
    },
    { key: 'mine', label: 'Zuständigkeit', type: 'select', options: [{ value: '', label: 'Alle Aufgaben' }, { value: '1', label: 'Meine Aufgaben (zuständig oder beteiligt)' }] },
    { key: 'assigned', label: 'Zuständig', type: 'select', options: [{ value: '', label: 'Alle Mitarbeiter' }, ...assignees.list.map((a) => ({ value: a.id, label: a.name }))] },
    { key: 'participant', label: 'Beteiligt', type: 'select', options: [{ value: '', label: 'Alle Mitarbeiter' }, ...assignees.list.map((a) => ({ value: a.id, label: a.name }))] },
  ];
  const emptyText = q ? 'Keine Aufgaben zu dieser Suche.' : group === 'done' ? 'Noch nichts erledigt.' : group === 'open' || group === 'nodate' ? 'Keine offenen Aufgaben.' : `Keine Aufgaben mit der Fälligkeit „${TASK_GROUP_LABELS[group]}“.`;

  return (
    <>
      <CalendarTabs />
      <div className="page-head">
        <h1>Aufgaben</h1>
        <span className="muted">{summary.data ? `${summary.data.open} offen` : ''}</span>
        {canWrite && <Link className="btn btn--primary btn--sm page-head__action" to="/kalender/aufgaben/neu">Neue Aufgabe</Link>}
      </div>

      <FilterBar
        search={{ value: q, placeholder: 'Im Titel suchen', onSubmit: (v) => update({ q: v }) }}
        fields={fields}
        // „Offen“ ist die Vorgabe und steht in der Adresszeile als leerer Wert, deshalb gilt sie nicht als gesetzter Filter
        values={{ group: group === 'open' ? '' : group, mine: mine ? '1' : '', assigned: assignedUserId ?? '', participant: participantUserId ?? '' }}
        onChange={(key, value) => update({ [key]: value })}
        onReset={() => setParams(new URLSearchParams())}
      />

      {list.isError && <div className="alert alert--error">{(list.error as Error).message}</div>}
      {toggle.error instanceof ApiError && <div className="alert alert--error">{toggle.error.message}</div>}

      <div className="card table-card">
        <table className="table table--cards">
          <thead>
            <tr>
              <th>Aufgabe</th>
              <th>Fällig</th>
              <th>Zuständig</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && (
              <tr>
                <td colSpan={4} className="muted">Wird geladen …</td>
              </tr>
            )}
            {list.data?.data.length === 0 && (
              <tr>
                <td colSpan={4} className="empty">{emptyText}</td>
              </tr>
            )}
            {list.data?.data.map((t) => {
              const done = t.status === 'done';
              const urgency = done ? ({ state: 'none', hint: null } as const) : urgencyOf(t.dueDate, today);
              const canToggle = canWrite || (!!session && t.assignedUserId === session.user.id);
              return (
                <tr
                  key={t.id}
                  className={`task-row${revealed === t.id ? ' is-revealed' : ''}${done ? '' : ` task-row--${urgency.state}`}`}
                  onPointerDown={(e) => onPointerDown(e, t.id)}
                  onPointerMove={onPointerMove}
                  onPointerUp={cancelTimer}
                  onPointerCancel={cancelTimer}
                  onPointerLeave={cancelTimer}
                  onContextMenu={(e) => isCardLayout() && e.preventDefault()}
                  onClickCapture={(e) => {
                    if (suppressClick.current) {
                      e.preventDefault();
                      e.stopPropagation();
                      suppressClick.current = false;
                    }
                  }}
                >
                  <td>
                    <div className="task-actions" role="group" aria-label={`Aktionen für „${t.title}“`}>
                      {canToggle && (
                        <button
                          type="button"
                          className="icon-btn"
                          title={done ? 'Wieder öffnen' : 'Erledigt'}
                          aria-label={done ? 'Wieder öffnen' : 'Als erledigt markieren'}
                          disabled={toggle.isPending}
                          onClick={() => {
                            toggle.mutate(t);
                            setRevealed(null);
                          }}
                        >
                          {done ? <UndoIcon /> : <CheckIcon />}
                        </button>
                      )}
                      <Link className="icon-btn" to={`/kalender/aufgaben/${t.id}`} title="Bearbeiten" aria-label="Bearbeiten">
                        <EditIcon />
                      </Link>
                      {can('calendar.delete') && (
                        <button
                          type="button"
                          className="icon-btn icon-btn--danger"
                          title="Löschen"
                          aria-label="Löschen"
                          disabled={remove.isPending}
                          onClick={() => {
                            setRevealed(null);
                            if (window.confirm(`Aufgabe „${t.title}“ wirklich löschen?`)) remove.mutate(t.id);
                          }}
                        >
                          <TrashIcon />
                        </button>
                      )}
                    </div>
                    <Link to={`/kalender/aufgaben/${t.id}`} className={`strong-link${done ? ' task-title--done' : ''}`}>{t.title}</Link>
                    {done && t.completedAt && (
                      <div className="muted small task-meta">
                        Erledigt{assignees.name(t.completedByUserId) ? ` von ${assignees.name(t.completedByUserId)}` : ''} am {completedFormat.format(new Date(t.completedAt))}
                      </div>
                    )}
                  </td>
                  <td data-label="Fällig" className={`nowrap task-due task-due--${urgency.state}`}>
                    {t.dueDate ? formatDate(t.dueDate) : <span className="muted">–</span>}
                    {urgency.hint && <div className="small task-due__hint">{urgency.hint}</div>}
                  </td>
                  <td data-label="Zuständig">
                    <div>
                      {assignees.name(t.assignedUserId) ?? <span className="muted">{t.assignedUserId ? 'Nicht mehr verfügbar' : 'Niemand'}</span>}
                      {t.participantUserIds.length > 0 && (
                        <div className="muted small task-meta">
                          Mit {t.participantUserIds.map((x) => assignees.name(x) ?? 'Nicht mehr verfügbar').join(', ')}
                        </div>
                      )}
                    </div>
                  </td>
                  <td data-label="Status" className="task-status-cell">
                    <span className={`task-badge task-badge--${done ? 'done' : urgency.state}`}>{done ? 'Erledigt' : URGENCY_LABEL[urgency.state]}</span>
                    {canToggle && (
                      <button type="button" className="btn btn--secondary btn--sm task-status-btn" disabled={toggle.isPending} onClick={() => toggle.mutate(t)} aria-label={done ? `„${t.title}“ wieder öffnen` : `„${t.title}“ als erledigt markieren`}>
                        {done ? 'Wieder öffnen' : '✓ Erledigen'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {list.data && list.data.meta.lastPage > 1 && (
        <nav className="pager" aria-label="Seiten">
          <button className="btn btn--secondary btn--sm" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>Zurück</button>
          <span className="muted">Seite {page} von {list.data.meta.lastPage}</span>
          <button className="btn btn--secondary btn--sm" disabled={page >= list.data.meta.lastPage} onClick={() => update({ page: String(page + 1) })}>Weiter</button>
        </nav>
      )}
    </>
  );
}
